import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import GallerySection from '../components/GallerySection';
import TestimonialsSection from '../components/TestimonialsSection';
import { imageUrl } from '../lib/images';

export default function EventsPage() {
  return (
    <>
      <SEO
        title="Events"
        description="Eventfotograf og videograf til erhverv, festivaler, konferencer og kultur. Få professionelt visuelt materiale til markedsføring og SoMe."
      />

      {/* Hero Section (Cinematic Dark) */}
      <section className="relative min-h-[75vh] flex items-center justify-center pt-28 pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={imageUrl('/_assets/75513566-168614701331698-9032259809111210225-n.jpg', 'width=1920,quality=85,format=auto')}
            alt="Stemningsfuldt arrangement og fejring i Danmark"
            className="w-full h-full object-cover filter brightness-[0.4] contrast-[1.05]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0C0C0D] via-[#0C0C0D]/40 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 text-center space-y-6">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-serif font-medium text-[#F4EFEA] tracking-tight leading-tight max-w-4xl mx-auto">
            Levende dokumentation af jeres næste arrangement
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-zinc-300 font-sans max-w-2xl mx-auto leading-relaxed font-normal">
            Stærke eventbilleder dokumenterer ikke kun, hvem der var der – de formidler stemningen, energien og værdien af jeres arrangement.
          </p>

          <div className="pt-2 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/kontakt?ydelse=events"
              className="cta-btn inline-flex items-center justify-center px-8 py-3.5 bg-white hover:bg-stone-100 text-[#0C0C0D] font-semibold text-sm tracking-wide transition-all shadow-lg"
            >
              Book foto/video til jeres event
            </Link>
            <a
              href="#galleri"
              className="cta-btn inline-flex items-center justify-center px-8 py-3.5 bg-transparent hover:bg-transparent text-white font-medium text-sm tracking-wide transition-all border border-white"
            >
              Se eventgalleriet
            </a>
          </div>
        </div>
      </section>

      {/* Light Editorial Story / Value for Business Section */}
      <section className="py-20 md:py-28 bg-white text-[#141416] border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="md:col-span-5 aspect-[4/5] rounded-none overflow-hidden border border-zinc-200 shadow-lg">
              <img
                src={imageUrl('/_assets/142299669-247059033487264-5481047819933199986-n.jpg', 'width=800,quality=85,format=auto')}
                alt="Autentisk samtale og stemning til event"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="md:col-span-7 space-y-6">
              <h2 className="text-3xl sm:text-4xl font-serif text-[#141416] leading-tight">
                Billeder der arbejder for jeres brand længe efter eventet er slut
              </h2>
              <div className="space-y-4 text-[#3A3836] text-sm md:text-base leading-relaxed font-sans">
                <p>
                  Et vellykket event efterlader et stærkt indtryk hos deltagerne, men uden den rette dokumentation fordufter energien hurtigt.
                </p>
                <p>
                  Med Lunde Media som fotograf eller videograf får I professionelt produceret billed- og videomateriale, der fanger de ægte smil, det engagerede publikum, oplægsholdernes pondus og receptionens nærvær.
                </p>
                <p>
                  Dette arkiv er guld værd for jeres fremtidige markedsføring, sociale medier, nyhedsbreve og ved billetsalg og sponsorarbejde for næste års arrangement.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4 border-t border-zinc-200 text-xs text-[#575450]">
                <div className="flex items-center gap-2.5">
                  <span className="inline-block w-2 h-2 rounded-full bg-black shrink-0" style={{ borderRadius: '9999px' }} />
                  <span className="text-[#141416] font-medium">Konferencer & Erhvervsseminarer</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="inline-block w-2 h-2 rounded-full bg-black shrink-0" style={{ borderRadius: '9999px' }} />
                  <span className="text-[#141416] font-medium">Kultur, Koncerter & Festivaler</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="inline-block w-2 h-2 rounded-full bg-black shrink-0" style={{ borderRadius: '9999px' }} />
                  <span className="text-[#141416] font-medium">Jubilæer & Firmafester</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="inline-block w-2 h-2 rounded-full bg-black shrink-0" style={{ borderRadius: '9999px' }} />
                  <span className="text-[#141416] font-medium">Produktlanceringer & Messer</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic Gallery from CMS (Dark Photographic Mood) */}
      <div id="galleri">
        <GallerySection
          slug="events"
          title="Event- & Erhvervsgalleri"
          subtitle="Dokumentation · Mennesker & Stemning"
          className="bg-[#0C0C0D]"
        />
      </div>

      {/* Video & Stemningsfilm Section */}
      <section className="py-20 md:py-28 bg-[#111113] border-y border-white/10 text-[#F4EFEA]">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 space-y-6">
              <h2 className="text-3xl sm:text-4xl font-serif text-[#F4EFEA] leading-tight">
                Stemningsfilm & reels der vækker eventet til live
              </h2>
              <div className="space-y-4 text-zinc-300 text-sm md:text-base leading-relaxed">
                <p>
                  Stillbilleder fanger det gyldne øjeblik, men med levende video fanger du bevægelsen, latteren, talerne og den summen, der gør et event mindeværdigt.
                </p>
                <p>
                  Lunde Media producerer korte, engagerende videoklip og stemningsfilm på 15–60 sekunder. Perfekt skåret til Instagram Reels, LinkedIn og virksomhedens hjemmeside.
                </p>
              </div>

              <div className="pt-2 space-y-3 text-sm text-zinc-300">
                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-check text-[#B89B72]" aria-hidden="true" />
                  <span>Klipning tilpasset både 16:9 widescreen og 9:16 mobilformat</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-check text-[#B89B72]" aria-hidden="true" />
                  <span>Hurtig levering så materialet kan bruges mens eventet er friskt</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-check text-[#B89B72]" aria-hidden="true" />
                  <span>Kan kombineres som en samlet foto- og videopakke</span>
                </div>
              </div>

              <div className="pt-4">
                <Link
                  to="/kontakt?ydelse=events"
                  className="cta-btn inline-flex items-center gap-2 px-6 py-3.5 bg-white hover:bg-stone-100 text-black font-semibold text-sm tracking-wide transition-all shadow-lg"
                >
                  <span>Forespørg på eventvideo</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Video Player Display Container */}
            <div className="lg:col-span-7">
              <div className="relative aspect-video rounded-none overflow-hidden bg-black border border-white/15 shadow-2xl">
                <video
                  controls
                  playsInline
                  preload="metadata"
                  poster={imageUrl('/_assets/75513566-168614701331698-9032259809111210225-n.jpg', 'width=1200,quality=85,format=auto')}
                  className="w-full h-full object-cover"
                >
                  <source src="/_assets/bounce-2022-2-1.mp4" type="video/mp4" />
                  Din browser understøtter desværre ikke HTML5 video.
                </video>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Light Use Cases Section */}
      <section className="py-20 md:py-28 bg-white text-[#141416] border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-3">
            <h2 className="text-3xl sm:text-4xl font-serif text-[#141416]">
              Hvad kan materialet bruges til?
            </h2>
            <p className="text-zinc-600 text-sm md:text-base">
              Når Michael leverer jeres billeder og video, er det klar til brug direkte på tværs af jeres kanaler.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-8 rounded-none bg-white border border-zinc-200 shadow-sm space-y-4">
              <div className="text-black">
                <i className="fa-solid fa-share-nodes text-2xl text-black" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-serif text-[#141416]">Sociale Medier</h3>
              <p className="text-zinc-600 text-sm leading-relaxed">
                Opbyg engagement med levende reels, behind-the-scenes billeder og professionelle opslag på LinkedIn, Instagram og Facebook.
              </p>
            </div>

            <div className="p-8 rounded-none bg-white border border-zinc-200 shadow-sm space-y-4">
              <div className="text-black">
                <i className="fa-solid fa-globe text-2xl text-black" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-serif text-[#141416]">Website & Markedsføring</h3>
              <p className="text-zinc-600 text-sm leading-relaxed">
                Skift standardfotos ud med ægte billeder af jeres egne arrangementer, medarbejdere og kunder.
              </p>
            </div>

            <div className="p-8 rounded-none bg-white border border-zinc-200 shadow-sm space-y-4">
              <div className="text-black">
                <i className="fa-solid fa-newspaper text-2xl text-black" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-serif text-[#141416]">Presse & Partnere</h3>
              <p className="text-zinc-600 text-sm leading-relaxed">
                Send professionelt billedmateriale til lokalaviser, fagblade og sponsorer for at understøtte jeres PR-indsats.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Testimonials for Events & Business */}
      <TestimonialsSection
        category="events"
        title="Hvad arrangører og erhvervskunder siger"
        subtitle="Mød de forfattere, arrangører og virksomheder, Michael har hjulpet"
        theme="light"
      />

      {/* CTA Section (Cinematic Dark) */}
      <section className="py-20 bg-[#0C0C0D] border-t border-white/10 text-center">
        <div className="max-w-7xl mx-auto px-6 md:px-8 space-y-6">
          <h2 className="text-3xl sm:text-4xl font-serif text-[#F4EFEA] max-w-3xl mx-auto">
            Skal vi dække jeres næste arrangement?
          </h2>
          <p className="text-zinc-400 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto">
            Tag en uforpligtende snak med Michael om program, tidsrum og ønsket leverance (foto, video eller kombineret pakke).
          </p>
          <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt?ydelse=events"
              className="cta-btn inline-flex items-center justify-center px-8 py-4 bg-white hover:bg-stone-100 text-[#0C0C0D] font-semibold text-sm tracking-wide transition-all shadow-lg"
            >
              Få et uforpligtende tilbud
            </Link>
            <a
              href="tel:26368306"
              className="cta-btn inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent hover:bg-transparent text-white font-medium text-sm tracking-wide transition-all border border-white"
            >
              <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
              <span className="whitespace-nowrap">26 36 83 06</span>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
